// © Microsoft Corporation. All rights reserved.

import { ActionHandler, IActionPayload, IReducer } from "@msnews/experiences-redux";
import {
    CommonHeaderActions,
    UpdateSelectedNavItemSignature,
    UpdateSelectedTwoLevelNavItemSignature,
    UpdateIsTtvrFiredSignature
} from "./CommonHeader.actions";

import { CommonHeaderState } from "./CommonHeader.state";
import { NavigationItem } from "./CommonHeader.config";

/**
 * The reducer for the CommonHeader connector, and the CommonHeaderState
 */
export class CommonHeaderReducer implements IReducer<CommonHeaderState> {
    /**
     * Update the state for the <experience> connector, based on the action being applied
     * @param state The previous state
     * @param actionPayload The action being applied
     */
    public reduce(state: CommonHeaderState, actionPayload: IActionPayload): CommonHeaderState {
        // Return the initial state for this experience, including the config.
        if (!state) {
            return {
                isTtvrFired: false,
                selectedNavMenuItem: {} as NavigationItem,
                selectedNavItem: {} as NavigationItem,
                selectedSecondaryNavMenuItem: null,
                selectedSecondaryNavItem: null
            };
        }

        // Return the passed in state, if there is no action specified
        if (!actionPayload) {
            return state;
        }

        let newState: CommonHeaderState = state;
        let actionHandled = ActionHandler.handleAction<UpdateSelectedNavItemSignature>(
            actionPayload,
            CommonHeaderActions.updateSelectedNavItem,
            (selectedNavItem: NavigationItem, isContextualSecondaryNavEnabled = false) => {
                newState = {
                    ...state,
                    selectedNavItem: selectedNavItem,
                    selectedNavMenuItem: isContextualSecondaryNavEnabled ? selectedNavItem : null,
                    selectedSecondaryNavMenuItem: null,
                    selectedSecondaryNavItem: null };
            });

        actionHandled = actionHandled || ActionHandler.handleAction<UpdateSelectedTwoLevelNavItemSignature>(
            actionPayload,
            CommonHeaderActions.updateSelectedTwoLevelNavItems,
            (selectedNavMenuItem: NavigationItem, selectedNavItem: NavigationItem, selectedSecondaryNavMenuItem: NavigationItem, selectedSecondaryNavItem: NavigationItem) => {
                newState = { ...state, selectedNavItem: selectedNavItem, selectedSecondaryNavItem: selectedSecondaryNavItem, selectedNavMenuItem: selectedNavMenuItem, selectedSecondaryNavMenuItem: selectedSecondaryNavMenuItem };
            });

        actionHandled = actionHandled || ActionHandler.handleAction<UpdateIsTtvrFiredSignature>(
            actionPayload,
            CommonHeaderActions.updateIsTtvrFired,
            (isTtvrFired: boolean) => {
                newState = { ...state, isTtvrFired: isTtvrFired };
            });

        // Return the newState if one of the actionHandlers modified the state, otherwise return the original state
        return actionHandled ? newState : state;
    }
}